/**
 * Cart drawer and cart lines (drawer and cart page).
 *
 * <pg-cart-drawer> exposes open()/close() for @pg/cart's openCart and Shopify's standard
 * `openCart` action. <pg-cart-items> handles quantity steppers and remove buttons on
 * `[data-pg-cart-line]` rows: changes are debounced so tapping "+" three times sends one
 * request, the row dims while it updates, and a removed row slides out before the
 * re-rendered section replaces it.
 */
import { PgElement, define, debounce } from '@pg/core';
import { changeLine } from '@pg/cart';
import { openDialog, closeDialog } from '@pg/ui';

class PgCartDrawer extends HTMLElement {
  open() {
    openDialog('pg-cart-drawer');
  }

  close() {
    closeDialog(/** @type {HTMLDialogElement} */ (this.querySelector('dialog')));
  }
}

class PgCartItems extends PgElement {
  /** @type {Map<string, (quantity: number) => void>} */
  #pending = new Map();

  mount() {
    this.addEventListener('click', (event) => this.#onClick(event), { signal: this.signal });
    this.addEventListener('change', (event) => this.#onChange(event), { signal: this.signal });
  }

  /** @param {Element} line */
  #queue(line, quantity) {
    const key = line.getAttribute('data-key') || '';
    if (!this.#pending.has(key)) {
      this.#pending.set(
        key,
        debounce((qty) => this.#commit(line, qty), 350),
      );
    }
    this.#pending.get(key)?.(quantity);
  }

  async #commit(line, quantity) {
    const key = line.getAttribute('data-key') || '';
    this.#pending.delete(key);
    line.classList.add(quantity === 0 ? 'is-removing' : 'is-updating');
    try {
      await changeLine(key, quantity, { source: this });
    } catch {
      line.classList.remove('is-removing', 'is-updating');
    }
  }

  /** @param {Event} event */
  #onClick(event) {
    const target = /** @type {Element} */ (event.target);
    const line = target.closest('[data-pg-cart-line]');
    if (!line) return;

    if (target.closest('[data-pg-remove]')) {
      this.#commit(line, 0);
      return;
    }

    const step = target.closest('[data-pg-qty]');
    if (!step) return;
    const input = /** @type {HTMLInputElement} */ (line.querySelector('[data-pg-line-qty]'));
    const increment = Number(input.step) || 1;
    const next = Math.max(0, Number(input.value) + (step.getAttribute('data-pg-qty') === 'plus' ? increment : -increment));
    if (input.max && next > Number(input.max)) return;
    input.value = String(next);
    /** @type {HTMLButtonElement} */ (line.querySelector('[data-pg-qty="minus"]')).disabled = next <= 1;
    this.#queue(line, next);
  }

  /** @param {Event} event */
  #onChange(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    if (!input.matches('[data-pg-line-qty]')) return;
    const line = input.closest('[data-pg-cart-line]');
    if (line) this.#queue(line, Math.max(0, Number(input.value) || 0));
  }
}

define('pg-cart-drawer', PgCartDrawer);
define('pg-cart-items', PgCartItems);
